import type {
  AttachDocumentImageInput,
  CreatePassengerInput,
  PassengerDto,
  PassengerListPage,
  PassengerListQuery,
  SignedUrlDto,
  UpdatePassengerInput,
  UploadTargetDto,
} from "@tc/shared";
import { ApiError } from "@/features/trips/api";

const API_URL = import.meta.env.VITE_API_URL ?? "/api";

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    headers: { accept: "application/json", "content-type": "application/json" },
    ...init,
  });
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = (await res.json()) as { message?: string | string[] };
      if (body?.message)
        message = Array.isArray(body.message) ? body.message.join(", ") : body.message;
    } catch {
      /* non-JSON error body — keep the status message */
    }
    throw new ApiError(message, res.status);
  }
  return (res.status === 204 ? undefined : await res.json()) as T;
}

/**
 * Passenger roster calls (TW-3447). Club users get full CRUD over their own roster (RLS-scoped);
 * back office reads cross-club via `?clubId=` and manages a club's roster on its behalf (TRAV-2 —
 * create names the club via `clubId` in the body). The optional document-image flow is a three-step
 * dance — sign an upload URL, PUT the bytes straight to it, then attach the returned key — so the
 * image bytes never pass through this API's JSON layer and only the private key is persisted.
 */
export const rosterApi = {
  /** List the roster. `clubId` narrows to one club (back office only — RLS scopes club users). */
  list: (clubId?: string) =>
    req<PassengerDto[]>(`/passengers${clubId ? `?clubId=${clubId}` : ""}`),
  /**
   * One page of the roster for the paginated table. Passing `page` flips `/passengers` into envelope
   * mode (server pagination + sorting + role/name filter); the full-array `list` above stays for the
   * manifest builder + "add all players".
   */
  listPage: (query: PassengerListQuery) => {
    const qs = new URLSearchParams();
    if (query.clubId) qs.set("clubId", query.clubId);
    if (query.page != null) qs.set("page", String(query.page));
    if (query.pageSize != null) qs.set("pageSize", String(query.pageSize));
    if (query.sort) qs.set("sort", query.sort);
    if (query.dir) qs.set("dir", query.dir);
    if (query.role && query.role !== "all") qs.set("role", query.role);
    if (query.q) qs.set("q", query.q);
    return req<PassengerListPage>(`/passengers?${qs.toString()}`);
  },
  create: (body: CreatePassengerInput) =>
    req<PassengerDto>("/passengers", { method: "POST", body: JSON.stringify(body) }),
  update: (id: string, body: UpdatePassengerInput) =>
    req<PassengerDto>(`/passengers/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  remove: (id: string) => req<void>(`/passengers/${id}`, { method: "DELETE" }),

  /** Step 1: ask for a short-lived signed URL to upload a document image to. */
  signUpload: (id: string) =>
    req<UploadTargetDto>(`/passengers/${id}/document-image/upload-url`, { method: "POST" }),
  /** Step 3: attach the uploaded object (by key) to the passenger. */
  attachImage: (id: string, body: AttachDocumentImageInput) =>
    req<PassengerDto>(`/passengers/${id}/document-image`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  /** A short-lived signed URL to view the on-file document image. */
  imageUrl: (id: string) => req<SignedUrlDto>(`/passengers/${id}/document-image`),
  removeImage: (id: string) =>
    req<PassengerDto>(`/passengers/${id}/document-image`, { method: "DELETE" }),

  /** Step 2: PUT the raw file bytes straight to the signed upload URL (bypasses this API's JSON). */
  async uploadBytes(target: UploadTargetDto, file: File): Promise<void> {
    const res = await fetch(target.url, {
      method: target.method,
      headers: { "content-type": file.type || "application/octet-stream" },
      body: file,
    });
    if (!res.ok) throw new ApiError(`Upload failed (${res.status})`, res.status);
  },
};
